import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { 
  Briefcase, 
  Clock, 
  Search,
  ChevronLeft,
  RefreshCw,
  MapPin,
  Wrench,
  Calendar,
  Building2,
  Plus
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  "New": "bg-slate-100 text-slate-700",
  "Job Created": "bg-slate-100 text-slate-700",
  "PO Issued": "bg-blue-100 text-blue-700",
  "In Progress": "bg-blue-100 text-blue-700",
  "Awaiting Quote": "bg-amber-100 text-amber-700",
  "Quote Awaiting Approval": "bg-amber-100 text-amber-700",
  "Completed": "bg-green-100 text-green-700",
  "Closed": "bg-green-100 text-green-700",
};

const PRIORITY_STYLES = {
  "Emergency": "bg-red-100 text-red-700",
  "Urgent": "bg-orange-100 text-orange-700",
  "High": "bg-amber-100 text-amber-700",
  "Medium": "bg-blue-100 text-blue-700",
  "Low": "bg-slate-100 text-slate-700",
};

export default function ClientPortalV2JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();

  const loadJobs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      let url = `${API}/api/system-expansion/client-v2/jobs?limit=200`;
      if (statusFilter && statusFilter !== "all") {
        url += `&status=${encodeURIComponent(statusFilter)}`;
      }
      
      const res = await axios.get(url, { headers });
      setJobs(res.data.rows || []);
    } catch (err) {
      console.error("Failed to load jobs:", err);
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, [statusFilter]);

  // Filter by search
  const filteredJobs = jobs.filter(job => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      job.job_reference?.toLowerCase().includes(searchLower) ||
      job.description?.toLowerCase().includes(searchLower) ||
      job.park_name?.toLowerCase().includes(searchLower) ||
      job.unit_name?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-v2-jobs-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-700 to-teal-600 p-6 text-white">
        <div className="max-w-6xl mx-auto">
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-white hover:text-white hover:bg-teal-600 mb-2"
            onClick={() => navigate('/client-v2')}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold" data-testid="page-title">Jobs</h1>
          <p className="text-teal-100 text-sm mt-1">All maintenance jobs and requests</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search jobs..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Job Created">New</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Awaiting Quote">Awaiting Quote</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="ghost" size="sm" onClick={loadJobs} data-testid="refresh-btn">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              
              <Button 
                className="bg-teal-600 hover:bg-teal-700"
                onClick={() => navigate('/client-v2/jobs/new')}
                data-testid="new-job-btn"
              >
                <Plus className="h-4 w-4 mr-2" /> Report Issue
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Jobs List */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-teal-600" />
              Jobs ({filteredJobs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
              </div>
            ) : filteredJobs.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Briefcase className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No jobs found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredJobs.map((job) => (
                  <div 
                    key={job.job_id} 
                    className="border rounded-lg p-4 bg-white hover:shadow-sm transition-shadow cursor-pointer"
                    onClick={() => navigate(`/client-v2/jobs/${job.job_id}`)}
                    data-testid={`job-card-${job.job_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-teal-700">{job.job_reference}</span>
                          <Badge className={STATUS_STYLES[job.status] || "bg-slate-100 text-slate-700"}>
                            {job.status}
                          </Badge>
                          {job.priority && (
                            <Badge className={PRIORITY_STYLES[job.priority] || "bg-slate-100 text-slate-700"}>
                              {job.priority}
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-slate-700 mb-2">{job.description}</p>
                        
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          {job.park_name && (
                            <span className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {job.park_name}
                            </span>
                          )}
                          {job.unit_name && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {job.unit_name}
                            </span>
                          )}
                          {job.created_at && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(job.created_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {job.contractor_name && (
                        <div className="text-right text-sm">
                          <div className="text-slate-500">Contractor</div>
                          <div className="font-medium">{job.contractor_name}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
